/**
 * Pricing logic for listPrice salePrice and kitPrice
 *
 * @module views/components/Price
 * @memberof -Common
 */
import './Price.scss';

import React from 'react';

import PropTypes from 'prop-types';
import { Price as PalProductPricing } from 'web-palette/dist/components/Price/Price';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import interpolateString from '@ulta/utils/interpolateString/interpolateString';

import * as utils from './Price';

/**
  * Represents a Price component
  *
  * @method
  * @param {PriceProps} props - React properties passed from composition
  * @returns Price
  */
export const Price = function( props ){
  const {
    listPrice,
    originalPriceLabel,
    listPriceAccessibilityLabel,
    salePrice,
    salePriceAccessibilityLabel,
    kitPrice,
    variant = defaultProps.variant,
    priceLabel,
    message,
    appDownloadLabel,
    appDownloadAction,
    componentKey,
    disabled,
    noPrice
  } = props;

  const { inflection } = useDeviceInflection();
  const accessibilityLabelSalePrice = utils.getAccessibilityLabel( { label: salePriceAccessibilityLabel, values: [salePrice] } );
  const accessibilityLabelListPrice = utils.getAccessibilityLabel( { label: listPriceAccessibilityLabel, values: [listPrice] } );
  const kitPriceAccessibilityLabel = ( kitPrice && `Kit Price ${ kitPrice }` );
  const appOnlyDownloadLabel = inflection.MOBILE && appDownloadLabel;
  const externalAppDownloadAction = utils.getExternalAppDownloadAction( { inflection: inflection, appDownloadAction: appDownloadAction } );
  const derivedAppDownloadAction = appOnlyDownloadLabel ? appDownloadAction : null;

  return (
    <div className='Price'>
      <PalProductPricing
        listPriceAccessibilityLabel={ accessibilityLabelListPrice }
        salePriceAccessibilityLabel={ accessibilityLabelSalePrice }
        appDownloadLabel={ appOnlyDownloadLabel }
        appDownloadAction={ derivedAppDownloadAction }
        externalAppDownloadAction={ externalAppDownloadAction }
        kitPriceAccessibilityLabel={ kitPriceAccessibilityLabel }
        listPrice={ listPrice }
        kitPrice={ kitPrice }
        priceLabel={ priceLabel }
        salePrice={ salePrice }
        message={ message }
        originalPriceLabel={ originalPriceLabel }
        disabled={ disabled }
        variant={ variant }
        noPrice={ noPrice }
        key={ componentKey }
      />
    </div>

  );
};

/**
   * @method getExternalAppDownloadAction
   * @summary This method will build the External App Download Action link for mobile devices
   * @param {object} data - data object
   * @param {object} data.inflection - device inflection object
   * @param {object} data.appDownloadAction - app download action configuration
   */
export const getExternalAppDownloadAction = ( data ) => {
  const { inflection, appDownloadAction } = data;
  if( !inflection.MOBILE || !appDownloadAction ){
    return null;
  }
  return (
    <Link_Huge
      compact
      action={ appDownloadAction }
      className={ 'Price__appOnly--downloadTheApp' }
    >
      <Text
        htmlTag='span'
        textStyle='body-3'
      >
        { appDownloadAction.label }
      </Text>
    </Link_Huge>
  );
};

/**
   * @method getAccessibilityLabel
   * @summary This method will build the Accessibility Label for screen readers
   * @param {object} data - data object
   * @param {string} data.label - accessibility label template
   * @param {string} data.value - price value to interpolate into the label
   */
export const getAccessibilityLabel = ( data ) => {
  const { label, values } = data;
  if( !label || !values ){
    return null;
  }
  return interpolateString( label, values );
};

/**
  * property type definitions
  * @typedef PriceProps
  * @type {object}
  * @property {string} listPrice - Sets the list price to display
  * @property {string} originalPriceLabel - Sets the original price text to display with list price
  * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label for screen readers
  * @property {string} salePrice - Sets the sale price to display
  * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label for screen readers
  * @property {string} kitPrice - Sets the kit value to display
  * @property {string} variant - Sets the variant of the UI component.  Either `largeStacked` (default), `large`, or `compact`
  * @property {boolean} compact - Flag to render the compact size
  * @property {string} message - Sets the message to display
  * @property {string} priceLabel - Sets the price label to display
  * @property {string} appDownloadLabel - Sets the app download label to display
  * @property {object} appDownloadAction - Sets the app download action
  * @property {string} componentKey - Sets the componentKey
  * @property {bool} disabled - Sets the disabled value
  * @property {object} noPrice - Sets the noPrice value
  */
export const propTypes = {

  /** Sets the list price to display */
  listPrice: PropTypes.string,
  /** Sets the original price text to display with list price */
  originalPriceLabel: PropTypes.string,
  /** Sets the list price accessibility label for screen readers */
  listPriceAccessibilityLabel: PropTypes.string,
  /** Sets the sale price to display */
  salePrice: PropTypes.string,
  /** Sets the sale price accessibility label for screen readers */
  salePriceAccessibilityLabel: PropTypes.string,
  /** Sets the kit value to display */
  kitPrice: PropTypes.string,
  /** Sets the variant of the UI component. Either `PDP`, `large`, or `default` */
  variant: PropTypes.string,
  /** Sets the message to display */
  message: PropTypes.string,
  /** Sets the price label to display */
  priceLabel: PropTypes.string,
  /** Sets the app download label to display */
  appDownloadLabel: PropTypes.string,
  /** Sets the app download action */
  appDownloadAction: PropTypes.object,
  /** Sets the componentKey */
  componentKey: PropTypes.string,
  /** Sets the disabled state of the price */
  disabled: PropTypes.bool,
  /** No Info on this Prop Yet */
  noPrice: PropTypes.object
};

/**
  * Default values for passed properties
  * @type {object}
  * @property {string} variant='default' - The default variant of the UI component
  */
export const defaultProps = {
  variant: 'default'
};

Price.propTypes = propTypes;
Price.displayName = 'Price';
Price.defaultProps = defaultProps;

export default Price;
